import { Nullable } from "@babylonjs/core/types";
import { VertexBuffer } from '@babylonjs/core/Meshes/buffer'
import { DataBuffer } from '@babylonjs/core/Meshes/dataBuffer'
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Effect } from '@babylonjs/core/Materials/effect';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Material } from '@babylonjs/core/Materials/material';
import { Constants } from '@babylonjs/core/Engines/constants';
import { observeOnceToPromise } from "../utilities";

export type TextureSize = number | { width: number, height: number };
export type MixTextureOptions = {
  generateMipMaps?: boolean
  samplingMode?: number
}

export type Layer = {
  name: string
  order: LayerOrder,
  texture: Texture
  mask?: Texture
  baseColor: Color4
  blend: boolean
}

const SAMPLER_LAYER_TEX = 'layerTex';
const SAMPLER_MASK_TEX = 'maskTex';
const SAMPLER_BACKGROUND_TEX = 'backgroundTex';
const UNIFORM_BASE_COLOR = 'baseColor';
const UNIFORM_CLEAR_COLOR = 'clearColor';

Effect.ShadersStore["layeredTextureVertexShader"] = `
// Attributes
attribute vec2 position;
attribute vec2 uv;

// Output
varying vec2 vUV;

void main(void) {
  gl_Position = vec4(position, 0.0, 1.0);
  vUV = uv;
}
`
const fragmentTemplate = (main: string) => {
  return `
  precision highp float;
  
  // Inputs
  varying vec2 vUV;
  
  // Uniforms
  uniform vec4 ${UNIFORM_BASE_COLOR};
  uniform sampler2D ${SAMPLER_BACKGROUND_TEX};
  uniform sampler2D ${SAMPLER_LAYER_TEX};
  uniform sampler2D ${SAMPLER_MASK_TEX};
  
  void main(void) {
    // background image comes in flipped. We are unflipping here
    vec4 base = texture2D(${SAMPLER_BACKGROUND_TEX}, vec2(0, 1) + (vUV * vec2(1, -1)));
    vec4 layer = texture2D(${SAMPLER_LAYER_TEX}, vUV) * ${UNIFORM_BASE_COLOR};
    float mask = texture2D(${SAMPLER_MASK_TEX}, vUV).r;
  
    ${main}
  }
  `
}
Effect.ShadersStore["layeredTextureAlbedoFragmentShader"] = fragmentTemplate(`
  gl_FragColor = mix(base, layer, layer.a * mask);
`);

Effect.ShadersStore["layeredTextureDataFragmentShader"] = fragmentTemplate(`
  gl_FragColor = mix(base, layer, mask);
`);

export type LayerOrder = "Base" | "Face Plate" | "Face-Plate" | "Art" | "Ring";

export class LayeredTexture extends Texture {
  private _size: TextureSize;
  private _vertexBuffers: { [key: string]: VertexBuffer } = {};
  private _indexBuffer?: DataBuffer;
  private _generateMipMaps: boolean;
  private _albedoLayerEffect: Effect;
  private _dataLayerEffect: Effect;
  private _onReady: Promise<[void, void]>;
  private _emptyTexture: Texture;
  private _defaultMask: Texture;
  private _mask?: Texture;
  private _background: Texture;

  public _engine: Engine;
  public layers: Layer[] = [];

  public clearColor: Color4 = new Color4(0, 0, 1, 0);

  public layerOrder: { [name in LayerOrder]: number } = {
    "Base": 0,
    "Face Plate": 1,
    "Face-Plate": 1,
    "Art": 2,
    "Ring": 3
  }

  constructor(name: string, size: TextureSize, scene: Scene, options: MixTextureOptions = {}, public readonly isEmissive: boolean = false ) {
    super(null, scene, !options.generateMipMaps, false, options.samplingMode);

    this.name = name;
    this._engine = scene.getEngine();
    const effectCommonParams: [string[], string[], string[]] = [
      [VertexBuffer.PositionKind, VertexBuffer.UVKind],
      [
        UNIFORM_CLEAR_COLOR,
        UNIFORM_BASE_COLOR,
      ],
      [
        SAMPLER_BACKGROUND_TEX,
        SAMPLER_LAYER_TEX,
        SAMPLER_MASK_TEX,
      ]
    ]

    // Effect specific for albedo texture which needs blending on alpha
    this._albedoLayerEffect = this._engine.createEffect(
      { vertex: 'layeredTexture', fragment: 'layeredTextureAlbedo'},
      ...effectCommonParams
    );

    // Effect specific for layers that should not blend
    this._dataLayerEffect = this._engine.createEffect(
      { vertex: 'layeredTexture', fragment: 'layeredTextureData'},
      ...effectCommonParams
    );

    this._size = size;

    // The internal texture used to combine all layers
    this._texture = this._engine.createRenderTargetTexture(this._size, {
      samplingMode: Constants.TEXTURE_TRILINEAR_SAMPLINGMODE,
      generateDepthBuffer: false,
      generateStencilBuffer: false
    });

    // background texture used during continuous layer rendering
    this._background = new Texture(null, scene, false, false, Constants.TEXTURE_NEAREST_SAMPLINGMODE);
    this._background._texture = this._engine.createRenderTargetTexture(this._size, {
      samplingMode: Constants.TEXTURE_TRILINEAR_SAMPLINGMODE,
      generateDepthBuffer: false,
      generateStencilBuffer: false
    });

    // empty texture for default albedo values
    this._emptyTexture = new Texture(null, scene, false, false, Constants.TEXTURE_NEAREST_SAMPLINGMODE);
    this._emptyTexture._texture = this._engine.createRenderTargetTexture(1, false);

    // default white mask for default mask
    this._defaultMask = new Texture(null, scene, false, false, Constants.TEXTURE_NEAREST_SAMPLINGMODE);
    this._defaultMask._texture = this._engine.createRenderTargetTexture(1, false);
    
    // Here we create a screen quad to perform all layered rendering work
    // VBO
    let vertices = [ -1,  1,  1,  1,  1,  -1,  -1, -1 ];
    let uvs      = [  0,  0,  1,  0,  1,  1,  0,  1 ];
    let indices  = [  0,  1,  2,  0,  2,  3 ];
    this._vertexBuffers[VertexBuffer.PositionKind] = new VertexBuffer(this._engine, vertices, VertexBuffer.PositionKind, false, false, 2);
    this._vertexBuffers[VertexBuffer.UVKind] = new VertexBuffer(this._engine, uvs, VertexBuffer.UVKind, false, false, 2);
    this._indexBuffer = this._engine.createIndexBuffer(indices);
    this.wrapU = 1;
    this.wrapV = 1;
    this.wrapR = 1;
    this._generateMipMaps = !!options.generateMipMaps;

    ////////////////////////////////////////////////////////////////////////
    // put effect compile on a promise to track as one event
    const albedoEffectCompile = new Promise<void>((resolve, reject) => {
      this._albedoLayerEffect.executeWhenCompiled(() => {
        resolve();
      });
      this._albedoLayerEffect.onErrorObservable.addOnce((eventData, eventState) => {
        reject(this._albedoLayerEffect.getCompilationError());
      });
    });

    const dataEffectCompile = new Promise<void>((resolve, reject) => {
      this._dataLayerEffect.executeWhenCompiled(() => {
        resolve();
      });
      this._dataLayerEffect.onErrorObservable.addOnce((eventData, eventState) => {
        reject(this._albedoLayerEffect.getCompilationError());
      });
    });

    // Wait for all effects to compile
    this._onReady = Promise.all([albedoEffectCompile, dataEffectCompile]);
  }

  public clearLayers() {
    this.layers = [];
  }

  public addLayer(
    order: LayerOrder,
    label: string, 
    texture: Texture, 
    blend: boolean = true,
    baseColor: Color4 = new Color4(1, 1, 1, 1),
    mask?: Texture,
  ) {
    this.removeLayer(order);
    this.layers.push({
      name: `${this.name}.${order}.${label}`,
      texture,
      blend,
      baseColor,
      mask,
      order
    });

    this.layers = this.layers.sort(this.layerSorter.bind(this));
  }

  public removeLayer(order: LayerOrder) {
    this.layers = this.layers
      .filter(l => l.order != order)
      .sort(this.layerSorter.bind(this));
  }

  layerSorter(a: Nullable<Layer>, b: Nullable<Layer>) {
    if ( a && b ) {
      return this.layerOrder[a.order] - this.layerOrder[b.order];
    }

    return 0;
  }

  public isReady(): boolean {
    return this._albedoLayerEffect.isReady() && this._dataLayerEffect.isReady() && super.isReady();
  }

  public async onReady(): Promise<LayeredTexture> {
    await this._onReady;
    return this;
  }

  public clear(): void {

    if ( this._texture ) {
      this._engine.bindFramebuffer(this._texture);
      this._engine.clear(this.clearColor, true, false, false);
      this._engine.unBindFramebuffer(this._texture, !this._generateMipMaps);
    }

    if ( this._emptyTexture._texture ) {
      this._engine.bindFramebuffer(this._emptyTexture._texture);
      this._engine.clear(new Color4(.5, .5, 1, 0), true, false, false);
      this._engine.unBindFramebuffer(this._emptyTexture._texture, !this._generateMipMaps);
    }

    if ( this._defaultMask._texture ) {
      this._engine.bindFramebuffer(this._defaultMask._texture);
      this._engine.clear(new Color4(1, 1, 1, 1), true, false, false);
      this._engine.unBindFramebuffer(this._defaultMask._texture, !this._generateMipMaps);
    }
  }

  public async draw() {
    try {
      await this.onReady();
      for(let layer of this.layers) {
        if ( layer && layer.texture ) {
          if ( !layer.texture.isReady()) {
            await observeOnceToPromise(layer.texture.onLoadObservable);
          }
        }
      }

      if ( this._texture && this._background._texture && this._indexBuffer ) {
        let engine = this._engine;
        let gl = engine._gl;

        this.clear();
        engine.setState(false, undefined, true);
        engine.bindFramebuffer(this._background._texture, 0, undefined, undefined, true);

        for(let layer of this.layers) {
          engine.clear(this.clearColor, true, false, false);
          let effect = layer.blend?this._albedoLayerEffect:this._dataLayerEffect;

          engine.enableEffect(effect);
          engine.bindBuffers(this._vertexBuffers, this._indexBuffer, effect);

          effect.setTexture(SAMPLER_BACKGROUND_TEX, this);
          effect.setTexture(SAMPLER_LAYER_TEX, layer.texture);
          if ( layer.mask ) {
            effect.setTexture(SAMPLER_MASK_TEX, layer.mask);
          } else {
            effect.setTexture(SAMPLER_MASK_TEX, this._defaultMask);
          }

          if ( this.isEmissive ) {
            effect.setDirectColor4(UNIFORM_BASE_COLOR, new Color4(1, 1, 1, 1));
          } else {
            effect.setDirectColor4(UNIFORM_BASE_COLOR, layer.baseColor);
          }

          engine.drawElementsType(Material.TriangleFillMode, 0, 6);

          // copy result from backbuffer to this texture
          engine._bindTextureDirectly(gl.TEXTURE_2D, this._texture, true, true);
          gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0, this._texture.width, this._texture.height, 0);
        }

        engine.unBindFramebuffer(this._background._texture, !this._generateMipMaps);

      }
    } catch (err) {
      console.log("Error while updating mix texture");
      console.error(err);
    }
  }

  public dispose(): void {
    let scene = this.getScene();

    if ( !scene) {
      return;
    }

    Object.values(this._vertexBuffers).forEach((vb) => vb.dispose());
    this._vertexBuffers = {};

    if ( this._indexBuffer ) {
      if ( this._engine._releaseBuffer(this._indexBuffer) ) {
        this._indexBuffer = undefined;
      }
    }

    super.dispose();
  }

}